import React, { useState } from 'react';
import { cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import { Grid, TableHead, TableRow, TableCell, TableBody, Checkbox, FormHelperText, Tooltip } from '@mui/material';
import { Button, Dialog } from '@totalsoft/rocket-ui';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { concatFailure, Validator, Failure, ValidationError, Success } from '@totalsoft/pure-validations';
import CardNoResultsWrapper from 'components/layout/CardNoResultsWrapper';
import { BoxActionsWrapper } from 'components/common/CommonStyles';
import { ContractSelectionTableWrapper } from './ProductSelectionStyles';

const ContractSelectionModal = ({ contractTypes, closeContractTypesModal, onSaveContractTypes, t }) => {
  const mandatoryContractTypeValidator = Validator(function mandatorySocialAddressValidator(content) {
    return isAtLeastOneContractType(content) ? Success : Failure(ValidationError(t('OfferEdit.FinancialProducts.ContractTypeValidation')));
  });
  const validatorContractType = [mandatoryContractTypeValidator] |> concatFailure;

  const [localContractTypes, setLocalContractTypes] = useState(cloneDeep(contractTypes));
  const [validationContractType, validateContractType] = useDirtyFieldValidation(validatorContractType);

  const changeContractSelection = idx => event => {
    setLocalContractTypes(
      Object.assign([], {
        ...localContractTypes,
        [idx]: {
          ...localContractTypes[idx],
          isSelected: event.target.checked
        }
      })
    );
  };

  const [checkAllContractTypes, setCheckAllContractTypes] = useState(false);

  const handleCheckAll = () => {
    setCheckAllContractTypes(!checkAllContractTypes);
    setTrueLocalContractTypes(!checkAllContractTypes);
  };

  const setTrueLocalContractTypes = checkAllContractTypes => {
    let newLocalContractTypes = Object.assign([], localContractTypes);
    newLocalContractTypes.forEach(checkedContract => {
      checkedContract.isSelected = checkAllContractTypes;
    });
    setLocalContractTypes(newLocalContractTypes);
  };

  const onSave = () => {
    if (!validateContractType(localContractTypes)) return;
    onSaveContractTypes(localContractTypes);
  };

  const isAtLeastOneContractType = contractTypes => {
    return contractTypes.filter(contractType => contractType.isSelected).length > 0;
  };

  const isAnyContractTypeChecked = localContractTypes?.some(e => e.isSelected === true);

  return (
    <Dialog
      open={true}
      aria-labelledby='contract-selection-dialog-title'
      maxWidth='xs'
      fullWidth
      disableEscapeKeyDown
      onClose={closeContractTypesModal}
      PaperProps={{ style: { overflowY: 'visible' } }}
      title={t('OfferEdit.FinancialProducts.Filter')}
      content={
        <>
          <Grid container>
            <Grid item xs={12} md={12} sm={12}>
              <CardNoResultsWrapper content={contractTypes}>
                <ContractSelectionTableWrapper>
                  <TableHead>
                    <TableRow>
                      <TableCell className='checkboxColumn'>
                        <Tooltip title={t('SelectAll')}>
                          <Checkbox checked={checkAllContractTypes} onChange={handleCheckAll} />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <strong>{t('OfferEdit.FinancialProducts.ContractType')}</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {localContractTypes?.map((contractType, index) => {
                      return (
                        <TableRow key={index}>
                          <TableCell className='checkboxColumn'>
                            <Checkbox checked={contractType?.isSelected} onChange={changeContractSelection(index)} />
                          </TableCell>
                          <TableCell>{contractType.contractTypeName}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </ContractSelectionTableWrapper>
              </CardNoResultsWrapper>
            </Grid>
          </Grid>{' '}
          {!isValid(validationContractType) && (
            <FormHelperText error={true} id='name-error-text'>
              {getErrors(validationContractType)}
            </FormHelperText>
          )}
        </>
      }
      actions={
        <BoxActionsWrapper forDialog>
          <Button
            id={'contractSelectionModal_filterBtn'}
            color='primary'
            variant='contained'
            onClick={() => {
              onSave();
            }}
            disabled={!isAnyContractTypeChecked}
          >
            {t('Select')}
          </Button>
          <Button id={'contractSelectionModal_closeBtn'} onClick={closeContractTypesModal}>
            {t('Close')}
          </Button>
        </BoxActionsWrapper>
      }
    />
  );
};

ContractSelectionModal.propTypes = {
  contractTypes: PropTypes.array.isRequired,
  closeContractTypesModal: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  onSaveContractTypes: PropTypes.func.isRequired,
  simulationsGenerated: PropTypes.bool.isRequired
};

export default compose(withTranslation())(ContractSelectionModal);
